import { create } from 'zustand';
import { Campaign } from "../types/campaign";
import { Template } from "../types/template";
import { PaymentMethod } from '../types/paymentMethod';
import { PaymentHistory } from "../types/paymentHistory";

// Dashboard Data

interface DashboardDataState {
    dashboardData: Campaign[];
    setDashboardData: (v: Campaign[]) => void;
    reset: () => void;
};

export const useDashboardDataStore = create<DashboardDataState>((set) => ({
    dashboardData: [],
    setDashboardData: (v: Campaign[]) => set(({ dashboardData: v })),
    reset: () => set({ dashboardData: [] }),
}));

// Manage Campaigns Data

type CampaignsDataType = {
    campaigns: Campaign[];
    templates: Template[];
}

interface CampaignsDataState {
    campaignsData: CampaignsDataType;
    setCampaignsData: (v: CampaignsDataType) => void;
    reset: () => void;
};

export const useCampaignsDataStore = create<CampaignsDataState>((set) => ({
    campaignsData: { campaigns: [], templates: [] },
    setCampaignsData: (v: CampaignsDataType) => set(({ campaignsData: v })),
    reset: () => set({ campaignsData: { campaigns: [], templates: [] } }),
}));

// Templates Data

interface TemplatesDataState {
    templatesData: Template[];
    setTemplatesData: (v: Template[]) => void;
    reset: () => void;
};

export const useTemplatesDataStore = create<TemplatesDataState>((set) => ({
    templatesData: [],
    setTemplatesData: (v: Template[]) => set(({ templatesData: v })),
    reset: () => set({ templatesData: [] }),
}));

// Analytics Data

type PerformanceType = {
    country: { [key: string]: number };
    platform: { [key: string]: number };
}

interface AnalyticsDataState {
    performance: PerformanceType;
    setPerformance: (v: PerformanceType) => void;
    campaigns: Campaign[];
    setCampaigns: (v: Campaign[]) => void;
    reset: () => void;
};

export const useAnalyticsDataStore = create<AnalyticsDataState>((set) => ({
    performance: { country: {}, platform: { iOS: 0, Android: 0 } },
    setPerformance: (v: PerformanceType) => set(({ performance: v })),
    campaigns: [],
    setCampaigns: (v: Campaign[]) => set(({ campaigns: v })),
    reset: () => set({
        performance: { country: {}, platform: { iOS: 0, Android: 0 } },
        campaigns: [],
    }),
}));

// Payment Data

interface PaymentState {
    paymentStatus: string;
    setPaymentStatus: (v: string) => void;
    paymentMethod: PaymentMethod | null;
    setPaymentMethod: (v: PaymentMethod | null) => void;
    paymentHistory: PaymentHistory | null;
    setPaymentHistory: (v: PaymentHistory | null) => void;
    reset: () => void;
};

export const usePaymentStore = create<PaymentState>((set) => ({
    paymentStatus: "pending",
    setPaymentStatus: (v: string) => set(({ paymentStatus: v })),
    paymentMethod: null,
    setPaymentMethod: (v: PaymentMethod | null) => set(({ paymentMethod: v })),
    paymentHistory: null,
    setPaymentHistory: (v: PaymentHistory | null) => set(({ paymentHistory: v })),
    reset: () => set({
        paymentStatus: "pending",
        paymentMethod: null,
        paymentHistory: null,
    }),
}));

// Reset Function

export function resetStores() {
    useDashboardDataStore.getState().reset();
    useCampaignsDataStore.getState().reset();
    useTemplatesDataStore.getState().reset();
    useAnalyticsDataStore.getState().reset();
    usePaymentStore.getState().reset();
}
